/**
 * Panneau d'administration — coquille (bloc 0).
 *
 * Affiche les chiffres clés (GET /admin/overview) et la structure des sections
 * à venir (blocs 1-2). L'accès est gardé par <RequireAdmin> (route) et par la
 * dépendance backend `admin_user` (source de vérité).
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { getAdminOverview } from "@/api/admin";

const KPI_LABELS: { key: keyof Awaited<ReturnType<typeof getAdminOverview>>; label: string }[] = [
  { key: "total_users", label: "Utilisateurs" },
  { key: "premium_users", label: "Premium" },
  { key: "pending_deletions", label: "Suppressions en attente" },
  { key: "total_logements", label: "Logements" },
  { key: "dpe_calcule", label: "DPE calculés" },
];

const SECTIONS = [
  {
    title: "Utilisateurs & RGPD",
    desc: "Recherche, tier, vérification, suppression RGPD, export",
    to: "/admin/users",
  },
  {
    title: "Moteur & paramètres",
    desc: "Distribution des étiquettes, versions moteur, prix (lecture seule)",
    to: "/admin/engine",
  },
  {
    title: "Feature flags",
    desc: "Interrupteurs runtime (sans redéploiement)",
    to: "/admin/flags",
  },
  {
    title: "Liens partenaires",
    desc: "Activer/désactiver chaque section de liens d'affiliation",
    to: "/admin/affiliations",
  },
  {
    title: "Seuils de rentabilité",
    desc: "Seuil ROI (années) par geste de travaux + défaut",
    to: "/admin/roi",
  },
  {
    title: "Prix de l'énergie",
    desc: "Tarifs €/kWh + abonnements, par énergie (sans redéploiement)",
    to: "/admin/energy-prices",
  },
  {
    title: "Page d'accueil",
    desc: "Choisir le modèle de home servi au public",
    to: "/admin/home-variant",
  },
  {
    title: "Simulation travaux",
    desc: "Choisir le modèle du panneau (V1 bouquets / V2 interactive)",
    to: "/admin/scenarios-variant",
  },
  {
    title: "Objectifs de rénovation",
    desc: "Classement des gestes et bouquets proposés par objectif",
    to: "/admin/renovation-objectives",
  },
  {
    title: "Santé système",
    desc: "Taille de la base, compteurs",
    to: "/admin/health",
  },
];

export default function AdminPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: getAdminOverview,
  });

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-6 py-12">
        <h1 className="text-2xl font-bold">Administration</h1>
        <p className="mt-1 text-sm text-gray-600">
          Panneau réservé aux comptes administrateurs.
        </p>

        {/* Chiffres clés */}
        <section className="mt-8">
          {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}
          {error && (
            <p className="text-sm text-red-700">Impossible de charger les chiffres clés.</p>
          )}
          {data && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {KPI_LABELS.map(({ key, label }) => (
                <div key={key} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="text-2xl font-bold text-blue-900">{data[key]}</div>
                  <div className="mt-1 text-xs uppercase tracking-wide text-gray-500">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Sections disponibles */}
        <section className="mt-10">
          <h2 className="text-lg font-semibold">Sections</h2>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {SECTIONS.map((s) => (
              <Link
                key={s.title}
                to={s.to}
                className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4 hover:border-blue-700 hover:shadow"
              >
                <div>
                  <div className="font-medium text-gray-800">{s.title}</div>
                  <div className="text-xs text-gray-500">{s.desc}</div>
                </div>
                <span aria-hidden="true" className="text-gray-400">→</span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}
